'use client'

import {
  createContext,
  useContext,
  useState,
  ReactNode,
  isValidElement,
  Children,
  useRef,
  useEffect
} from 'react'

import { useBreakpoints } from '@/shared/hooks/useBreakpoints'
import { cn } from '@/shared/lib/utils'

import { CardContext, CardVariant } from './Card'


type TabsContextType = {
  activeTab: string
  setActiveTab: (value: string) => void
}

const TabsContext = createContext<TabsContextType | undefined>(undefined)

const useTabsContext = () => {
  const context = useContext(TabsContext)
  if (!context) {
    throw new Error('useTabsContext must be used within TabsProvider')
  }
  return context
}

type TabItem = { value: string; label: ReactNode; disabled?: boolean }

function getTabItems(children: ReactNode): TabItem[] {
  return Children.map(children, child => {
    const props =
      isValidElement(child) && typeof child.props === 'object' && child.props !== null
        ? child.props
        : null
    if (props && 'value' in props)
      return {
        value: (props as { value: string }).value,
        label: (props as { children?: ReactNode }).children,
        disabled: (props as { disabled?: boolean }).disabled
      } as TabItem
    return null
  }).filter((x): x is TabItem => x != null)
}

function TabsOverflowTrigger({
  items,
  variant,
  onClose
}: {
  items: TabItem[]
  variant: CardVariant
  onClose: () => void
}) {
  const { activeTab, setActiveTab } = useTabsContext()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      )
        setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  const handleSelect = (value: string) => {
    setActiveTab(value)
    setOpen(false)
    onClose()
  }

  const activeInOverflow = items.some(i => i.value === activeTab)
  const triggerLabel = activeInOverflow
    ? items.find(i => i.value === activeTab)?.label ?? 'Mais'
    : 'Mais'

  const isDefaultOrPrimary = variant === 'default'
  const isYellow = variant === 'yellow'
  const outerClasses = cn(
    'relative rounded-md transition-all duration-100 cursor-pointer select-none shrink-0',
    isDefaultOrPrimary && 'bg-[#6A81DB]',
    variant === 'purple' && 'bg-[#78229f]',
    isYellow && 'bg-[#F39D72]',
    variant === 'lightBlue' && 'bg-[#5a9fc9]',
    variant === 'gray' && 'bg-card'
  )
  const innerClasses = cn(
    'relative top-0 left-0 w-full h-full flex items-center justify-center gap-1',
    'px-4 py-2 rounded-md font-sans font-semibold text-center transition-all duration-100 text-white',
    'translate-y-[-2px] active:translate-y-0 hover:translate-y-[-1px]',
    isDefaultOrPrimary &&
      'bg-white bg-[linear-gradient(360deg,_#EAEEFF_-12.43%,_#BAC4F0_143.3%)] inset-shadow-[0_4px_0_0_rgba(255,255,255,0.25)] text-primary-dark',
    variant === 'gray' &&
      'bg-card bg-[linear-gradient(360deg,_rgba(234,238,255,0.2)_-12.43%,_rgba(186,196,240,0.2)_143.3%)] text-white',
    isYellow &&
      'bg-white text-gray-800 bg-[linear-gradient(360deg,_#EAEEFF_-12.43%,_#F0DFBA_143.3%)] shadow-[0_4px_0_-1px_#F39D72,inset_0_2px_0_0_rgba(255,255,255,0.25)]',
    variant === 'purple' &&
      'bg-white bg-[linear-gradient(360deg,_#F0E6F8_-12.43%,_#D4B8E8_143.3%)] shadow-[0_5px_0_-1px_#78229f,inset_0_4px_0_rgba(255,255,255,0.25)] text-[#4a1560]',
    variant === 'lightBlue' &&
      'bg-white bg-[linear-gradient(360deg,_#E8F4FC_-12.43%,_#B8D7F0_143.3%)] shadow-[0_5px_0_-1px_#7eb8e0,inset_0_4px_0_rgba(255,255,255,0.25)] text-[#2a6b8f]'
  )

  return (
    <div className="relative shrink-0" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen(prev => !prev)}
        className={outerClasses}
        aria-expanded={open}
        aria-haspopup="true"
      >
        <div className={innerClasses}>
          <span>{triggerLabel}</span>
          <svg
            className={cn('size-4 transition-transform', open && 'rotate-180')}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </div>
      </button>
      {open && (
        <div
          className="absolute right-0 top-full z-20 mt-1 max-h-80 min-w-[140px] overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg"
          role="menu"
        >
          {items.map(({ value, label, disabled }) => (
            <button
              key={value}
              type="button"
              role="menuitem"
              disabled={disabled}
              onClick={() => !disabled && handleSelect(value)}
              className={cn(
                'w-full px-4 py-2 text-left text-sm font-medium text-gray-800 hover:bg-gray-100',
                activeTab === value && 'bg-primary/10 text-primary',
                disabled && 'cursor-not-allowed opacity-50'
              )}
            >
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export function Tabs({
  primaryValue,
  value,
  onValueChange,
  className = '',
  children,
  visibleCountMobile,
  visibleCountDesktop
}: {
  primaryValue?: string
  value?: string
  onValueChange?: (value: string) => void
  className?: string
  children: ReactNode
  visibleCountMobile?: number
  visibleCountDesktop?: number
}) {
  const [internalValue, setInternalValue] = useState(primaryValue || '')
  const activeTab = value ?? internalValue
  const { isMobile } = useBreakpoints()

  const setActiveTab = (newValue: string) => {
    if (onValueChange) {
      onValueChange(newValue)
    } else {
      setInternalValue(newValue)
    }
  }

  const tabItems = getTabItems(children)
  const childArray = Children.toArray(children)
  const useOverflowMobile =
    visibleCountMobile != null &&
    isMobile &&
    tabItems.length > visibleCountMobile
  const useOverflowDesktop =
    visibleCountDesktop != null &&
    !isMobile &&
    tabItems.length > visibleCountDesktop
  const useOverflow = useOverflowMobile || useOverflowDesktop
  const visibleCount = useOverflowMobile
    ? visibleCountMobile!
    : useOverflowDesktop
      ? visibleCountDesktop!
      : tabItems.length
  const visibleChildren = useOverflow
    ? childArray.slice(0, visibleCount)
    : childArray
  const overflowItems = useOverflow
    ? tabItems.slice(visibleCount)
    : []

  const cardContext = useContext(CardContext)
  const variant: CardVariant = cardContext?.variant ?? 'default'

  return (
    <TabsContext.Provider value={{ activeTab, setActiveTab }}>
      <div
        className={cn(
          'flex flex-wrap gap-2 sm:gap-5 p-1 w-full min-w-0',
          !useOverflow && 'sm:flex-nowrap',
          className
        )}
        role="tablist"
      >
        {visibleChildren}
        {useOverflow && overflowItems.length > 0 && (
          <TabsOverflowTrigger
            items={overflowItems}
            variant={variant}
            onClose={() => {}}
          />
        )}
      </div>
    </TabsContext.Provider>
  )
}

export function Tab({
  value,
  href,
  onClick,
  className = '',
  children,
  disabled = false
}: {
  value: string
  href?: string
  onClick?: () => void
  className?: string
  children: ReactNode
  disabled?: boolean
}) {
  const { activeTab, setActiveTab } = useTabsContext()
  const isActive = activeTab === value

  const cardContext = useContext(CardContext)
  const variant: CardVariant = cardContext?.variant ?? 'default'

  const handleClick = (e: React.MouseEvent) => {
    if (disabled) return

    if (!href) {
      e.preventDefault()
    }

    setActiveTab(value)
    onClick?.()
  }

  const isDefaultOrPrimary = variant === 'default'
  const isYellow = variant === 'yellow'
  const outerClasses = cn(
    'relative rounded-md transition-all duration-100 cursor-pointer select-none shrink-0 min-w-0',
    isActive && {
      'bg-primary': isDefaultOrPrimary,
      'bg-[#78229f]': variant === 'purple',
      'bg-[#E24137]': isYellow,
      'bg-[#5a9fc9]': variant === 'lightBlue',
      'bg-card': variant === 'gray'
    },
    !isActive && {
      'bg-[#6A81DB]': isDefaultOrPrimary,
      'bg-[#78229f]': variant === 'purple',
      'bg-[#F39D72]': isYellow,
      'bg-[#5a9fc9]': variant === 'lightBlue',
      'bg-card': variant === 'gray'
    },
    { 'opacity-50 cursor-not-allowed': disabled },
    className
  )

  const innerClasses = cn(
    'relative top-0 left-0 w-full h-full flex items-center justify-center',
    'px-4 py-2 rounded-md font-sans font-semibold text-center transition-all duration-100 text-white',
    {
      'bg-primary': isActive && isDefaultOrPrimary,
      'bg-[#78229f]': isActive && variant === 'purple',
      'bg-[#E24137]': isActive && isYellow,
      'bg-[#5a9fc9]': isActive && variant === 'lightBlue',
      'bg-card': isActive && variant === 'gray',

      'translate-y-[-2px] active:translate-y-0 hover:translate-y-[-1px]':
        !isActive,

      'bg-white bg-[linear-gradient(360deg,_#EAEEFF_-12.43%,_#BAC4F0_143.3%)] inset-shadow-[0_4px_0_0_rgba(255,255,255,0.25)] text-primary-dark':
        !isActive && isDefaultOrPrimary,
      'bg-card bg-[linear-gradient(360deg,_rgba(234,238,255,0.2)_-12.43%,_rgba(186,196,240,0.2)_143.3%)] inset-shadow-[0_4px_0_0_rgba(255,255,255,0.25)] text-white':
        !isActive && variant === 'gray',
      'bg-white text-gray-800 bg-[linear-gradient(360deg,_#EAEEFF_-12.43%,_#F0DFBA_143.3%)] shadow-[0_4px_0_-1px_#F39D72,inset_0_2px_0_0_rgba(255,255,255,0.25)]':
        !isActive && isYellow,
      'bg-white bg-[linear-gradient(360deg,_#F0E6F8_-12.43%,_#D4B8E8_143.3%)] shadow-[0_5px_0_-1px_#78229f,inset_0_4px_0_rgba(255,255,255,0.25)] text-[#4a1560]':
        !isActive && variant === 'purple',
      'bg-white bg-[linear-gradient(360deg,_#E8F4FC_-12.43%,_#B8D7F0_143.3%)] shadow-[0_5px_0_-1px_#7eb8e0,inset_0_4px_0_rgba(255,255,255,0.25)] text-[#2a6b8f]':
        !isActive && variant === 'lightBlue'
    }
  )

  if (href && !disabled) {
    return (
      <a href={href} onClick={handleClick} className={outerClasses}>
        <div className={innerClasses}>{children}</div>
      </a>
    )
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      className={outerClasses}
    >
      <div className={innerClasses}>{children}</div>
    </button>
  )
}
